<!-- =========================================================================================
  File Name: GlobalDiscounts.vue
  Description: Aplicación masiva de porcentaje de descuento a productos por categoría
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
  <div>
    <vx-card class="mb-2">

      <div slot="no-body" class="tabs-container pl-6 py-5">
        <div class="w-full vx-row mb-4">
          <div class="flex vx-col w-full items-center justify-end h-full">
            <div class="flex items-center justify-center w-48 h-12 rounded-sm bg-primary text-white cursor-pointer" @click="applyClicked">
              <vs-icon icon-pack="feather" icon="icon-percent" size="1.3rem" class="mr-2"/>
              <div class="mt-1">Aplicar</div>
            </div>
            <div class="flex items-center justify-center w-48 h-12 rounded-sm bg-danger text-white cursor-pointer" @click="reset_data">
              <vs-icon icon-pack="feather" icon="icon-corner-up-left" size="1.3rem" class="mr-2"/>
              <div class="mt-1">Limpiar</div>
            </div>
          </div>
        </div>
        <div class="w-full vx-row mb-4">
          <div class="vx-col w-full">
            <ejs-multiselect
              ref="categories"
              floatLabelType="Auto"
              placeholder="Categorías (dejar vacío para aplicar a todos los productos)"
              :dataSource="categories"
              mode="Box"
              :fields="{text: 'displayName', value: 'id'}"
              v-model="selectedCategories"
            ></ejs-multiselect>
          </div>
        </div>
        <div class="w-full vx-row mb-4">
          <div class="vx-col w-1/5">
            <ejs-numerictextbox
              floatLabelType="Auto"
              placeholder="% Descuento"
              format="N0"
              textAlign="Right"
              v-model="percentage"
              :min="0"
              :max="99"
              :step="1"
              :showSpinButton="false"
            ></ejs-numerictextbox>
          </div>
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
  import Vue from "vue";
  import {NumericTextBoxPlugin} from "@syncfusion/ej2-vue-inputs";
  import {MultiSelectPlugin} from "@syncfusion/ej2-vue-dropdowns";
  import {db} from '@/firebaseConfig';

  Vue.use(NumericTextBoxPlugin);
  Vue.use(MultiSelectPlugin);

  export default {
    data() {
      return {
        selectedCategories: [],
        percentage: 0
      }
    },
    computed: {
      categories() {
        return this.$store.getters.categoriesList;
      }
    },
    methods: {
      reset_data() {
        this.selectedCategories = [];
        this.percentage = 0;
      },
      // Se calcula la lista cruda de productos afectados usando el atributo
      // 'categories', que es un texto con los ids de categoría separados por
      // coma. Si no se selecciona ninguna categoría, se entiende que el
      // descuento se aplica a todos los productos.
      matchesSelectedCategories(product) {
        if (!this.selectedCategories || this.selectedCategories.length === 0) {
          return true;
        }
        if (!product.categories) {
          return false;
        }
        const productCategories = product.categories.split(',').map(id => id.trim()).filter(id => id !== '');
        return productCategories.some(id => this.selectedCategories.includes(id));
      },
      affectedProducts() {
        const list = (this.$store.state.products && this.$store.state.products.list) ? this.$store.state.products.list : [];
        return list.filter(product => this.matchesSelectedCategories(product));
      },
      applyClicked() {
        if (this.percentage === null || this.percentage === undefined || this.percentage === '') {
          this.$vs.notify({
            title: 'Datos incompletos',
            text: 'Debe indicar el porcentaje de descuento a aplicar',
            color: 'warning'
          });
          return;
        }

        const discount = Math.round(Number(this.percentage));
        if (isNaN(discount) || discount < 0 || discount > 99) {
          this.$vs.notify({
            title: 'Porcentaje inválido',
            text: 'El porcentaje de descuento debe ser un valor entre 0 y 99',
            color: 'warning'
          });
          return;
        }

        const affected = this.affectedProducts();
        if (affected.length === 0) {
          this.$vs.notify({
            title: 'Sin productos afectados',
            text: 'No se encontraron productos que pertenezcan a las categorías seleccionadas',
            color: 'warning'
          });
          return;
        }

        const categoriesText = (this.selectedCategories && this.selectedCategories.length > 0) ? this.categories.filter(category => this.selectedCategories.includes(category.id)).map(category => category.displayName).join(', ') : 'todos los productos';

        this.$vs.dialog({
          type: 'confirm',
          color: 'primary',
          title: 'Confirmar',
          text: `Se aplicará un descuento del ${discount}% a ${affected.length} producto(s) de: ${categoriesText}. ¿Desea continuar?`,
          accept: () => this.processDiscount(discount),
          acceptText: 'Aplicar',
          cancelText: 'Cancelar'
        });
      },
      processDiscount(discount) {
        this.$vs.loading({
          text: 'Aplicando descuento',
          type: 'point'
        });

        const self = this;
        const productsRef = db.collection('catalogs').doc('products');

        // Se usa una transacción para leer el documento justo antes de escribir
        // y evitar pisar ediciones concurrentes de otro administrador (p.ej.
        // desde Product.vue) hechas entre el momento en que se abrió esta
        // pantalla y el momento en que se confirma el descuento masivo. La
        // lista usada para calcular y aplicar los cambios es siempre la que se
        // lee dentro de la transacción, nunca el snapshot local de Vuex.
        let affectedCount = 0;
        db.runTransaction(transaction => {
          return transaction.get(productsRef).then(doc => {
            if (!doc.exists) {
              throw new Error('El documento de productos no existe');
            }
            const data = doc.data();
            const list = (data && data.list) ? data.list : [];
            affectedCount = 0;
            list.forEach(product => {
              if (self.matchesSelectedCategories(product)) {
                product.discount = discount;
                affectedCount++;
              }
            });
            transaction.update(productsRef, {list: list});
          });
        })
          .then(() => {
            self.$vs.notify({
              title: 'Descuento aplicado',
              text: `Se actualizó el descuento en ${affectedCount} producto(s) correctamente`,
              color: 'primary'
            });
            self.reset_data();
            self.$vs.loading.close();
          })
          .catch((error) => {
            self.$vs.notify({
              title: 'Error al aplicar descuento',
              text: error.message,
              color: 'danger'
            });
            self.$vs.loading.close();
          });
      }
    }
  }
</script>

<style lang="scss">
  @import '../../../node_modules/@syncfusion/ej2-base/styles/material.css';
  @import '../../../node_modules/@syncfusion/ej2-vue-dropdowns/styles/material.css';
  @import '../../../node_modules/@syncfusion/ej2-inputs/styles/material.css';
</style>
